import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTranslation } from '../hooks/useTranslation';

const Login: React.FC = () => {
    const { t } = useTranslation();
    const { loginUser, isAuthLoading } = useApp();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation() as any;

    const from = location.state?.from?.pathname || '/';

    const validate = (): string | null => {
        if (!email) return t('auth.emailRequired');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return t('auth.invalidEmail');
        if (!password) return t('auth.passwordRequired');
        if (password.length < 4) return t('auth.passwordMinLength');
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            await loginUser(email, password);
            navigate(from, { replace: true });
        } catch (err: any) {
            setError(err.message || t('auth.loginFailed'));
        }
    };

    return (
        <div className="container" style={{ padding: '4rem 0' }}>
            <motion.div 
                className="card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                style={{ maxWidth: 420, margin: '0 auto' }}
            >
                <h2 className="text-center" style={{ marginBottom: '1rem' }}>{t('auth.welcomeBack')}</h2>
                <p className="text-center text-gray-600" style={{ marginBottom: '1.5rem' }}>
                    {t('auth.signInToContinue')}
                </p>

                {error && (
                    <div className="error-container" style={{ padding: '0.75rem', border: '1px solid #dc2626', borderRadius: 8, marginBottom: 16 }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: 12 }}>
                        <label htmlFor="email" className="text-gray-700" style={{ display: 'block', marginBottom: 6 }}>{t('auth.email')}</label>
                        <input 
                            id="email"
                            type="email"
                            className="input"
                            placeholder={t('auth.emailPlaceholder')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label htmlFor="password" className="text-gray-700" style={{ display: 'block', marginBottom: 6 }}>{t('auth.password')}</label>
                        <input 
                            id="password"
                            type="password"
                            className="input"
                            placeholder={t('auth.passwordPlaceholder')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>

                    <motion.button 
                        type="submit"
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        disabled={isAuthLoading}
                    >
                        {isAuthLoading ? t('auth.signingIn') : t('auth.signIn')}
                    </motion.button>
                </form>

                <div className="text-center" style={{ marginTop: 12 }}>
                    <Link 
                        to="/forgot-password" 
                        style={{ 
                            fontSize: 14, 
                            color: 'var(--primary-color)', 
                            textDecoration: 'none' 
                        }}
                    >
                        {t('forgotPassword.title')}
                    </Link>
                </div>

                <p className="text-center text-gray-600" style={{ marginTop: 16, fontSize: 14 }}>
                    {t('auth.noAccount')} <Link to="/register">{t('auth.createAccount')}</Link>
                </p>
            </motion.div>
        </div>
    );
};

export default Login;
